import { Component, OnInit, output, input } from '@angular/core';
import { TaskSelectButton } from '../../../core/models/task-select-button.model';

import { THEMES } from '../../../core/models/theme.model';

@Component({
  selector: 'wf-task-select-button',
  templateUrl: './task-select-button.component.html',
  styleUrl: './task-select-button.component.css'
})
export class TaskSelectButtonComponent implements OnInit {
  readonly position = input<number>(1);

  readonly idx = input<number>(1);

  readonly taskSelect = input<TaskSelectButton | null>(null);

  readonly initializing = input<boolean>(true);

  readonly compareScores = input<boolean>(false);

  readonly alternating = input<boolean>(false);

  readonly isPreDiagnosticStudent = input<boolean>(false);

  readonly onTaskSelectClick = output<number>();

  readonly onTaskSelectTouchStart = output<{
    position: number;
    idx: number;
}>();

  readonly onTaskSelectTouchEnd = output<{
    position: number;
    idx: number;
    taskIdx: number;
    $event: TouchEvent;
}>();

  readonly onTaskMouseOver = output<{
    position: number;
    idx: number;
}>();

  readonly onTaskMouseLeave = output<{
    position: number;
    idx: number;
}>();

  themes = THEMES;
  scoreBoxes: {
    id: number,
    score: number,
    background: string,
    icon: string,
    progressed: boolean,
    completed: boolean,
    started: boolean,
    wordListType: number,
  }[] = [] ;
  wrapperGrids: string = '';
  formattedPassageTitle: string = '';
  passageTitleLineCount: number = 0;
  isLongPassageTitle: boolean = false;

  ngOnInit(): void {
    const completions = this.taskSelect()!.completions ;
    const subtasks = this.taskSelect()!.subtasks ;
    const suffixes = ['a', 'b', 'c', 'd'] ;
    const basePath = `/assets/images/taskSelect/${this.taskSelect()!.theme === THEMES.elementary ? '' : 'theme2/'}scoreboxes/` ;
    let completionIdxs: number[] = [] ;

    const taskSelect = this.taskSelect();
    if (taskSelect!.showPassageTitle)
    {
      this.initializePassageTitle(taskSelect!.passageTitle) ;

      // For passage tasks, we want to show the subtasks
      for (let idx = 0; idx < subtasks.length; idx++) {
        const { type, completed, progressed, started } = subtasks[idx];

        this.scoreBoxes.push({
          id: idx,
          score: 0,
          icon: `/assets/images/destination/iconPassageTask_${type}.svg`,
          background: basePath,
          progressed,
          completed,
          started,
          wordListType: 0,
        });
      }
    }
    else
    {
      // For other tasks, we show the completions
      for (let idx = 0; idx < completions.length; idx++) {
        const { score, completed, progressed, started, wordListType } = completions[idx];

        this.scoreBoxes.push({
          id: idx,
          score,
          icon: '',
          background: basePath,
          progressed,
          completed: taskSelect!.completed,
          started,
          wordListType,
        });

        if (completed) completionIdxs.push(idx) ;
      }

      if (this.scoreBoxes.length && !taskSelect!.completed)
      {
        this.scoreBoxes.push({
          id: this.scoreBoxes.length,
          score: 0,
          icon: '',
          background: basePath,
          progressed: false,
          completed: false,
          started: false,
          wordListType: 0,
        }) ;
      }
    }

    // This should not be necessary, but make sure we only have 4 max scoreboxes
    let completedIdx = completionIdxs.length > 1 ? completionIdxs[completionIdxs.length - 2] + 1 : 0 ;
    this.scoreBoxes = this.scoreBoxes.slice(completedIdx, completedIdx + 4) ;

    // Now that we have our final set, determine our backgrounds
    for (let idx = 0 ; idx < this.scoreBoxes.length ; idx++)
    {
      const taskCompletedBg = this.scoreBoxes[idx].completed ? 'gold' : 'default' ;
      const progressedBg = this.scoreBoxes[idx].progressed ? '_star' : '';
      const boxesStr = this.scoreBoxes.length > 1 ? `${this.scoreBoxes.length}${suffixes[idx] ?? 'a'}` : `${this.scoreBoxes.length}`;

      if (taskSelect!.showPassageTitle)
      {
        this.scoreBoxes[idx].background += `passageScoreBox-${boxesStr}-${taskCompletedBg}${progressedBg}.svg` ;
      }
      else
      {
        this.scoreBoxes[idx].background += `taskScoreBox-${boxesStr}-${taskCompletedBg}${progressedBg}.svg` ;
      }
    }

    this.wrapperGrids = `grids-${this.scoreBoxes.length}` ;
  }

  private initializePassageTitle(passageTitle: string | null): void {
    const title = passageTitle?.trim() ?? '' ;
    if (!title) return ;

    const lineLength = title.length >= 38 ? 18 : title.length >= 30 ? 16 : 14 ;
    this.formattedPassageTitle = this.formatPassageTitle(title, lineLength) ;
    this.passageTitleLineCount = this.formattedPassageTitle.split('\n').length ;
    this.isLongPassageTitle = title.length >= 30 || this.passageTitleLineCount >= 4 ;
  }

  private formatPassageTitle(value: string, lineLength: number): string {
    let remainingValue = value ;
    let lines: string[] = [] ;

    while (remainingValue.length > lineLength)
    {
      let endOfLineIndex = remainingValue.substring(0, lineLength).lastIndexOf(' ') ;
      if (endOfLineIndex <= 0) endOfLineIndex = lineLength ;

      lines.push(remainingValue.substring(0, endOfLineIndex)) ;

      let startOfLineIndex = remainingValue.indexOf(' ', endOfLineIndex) + 1 ;
      if (startOfLineIndex < endOfLineIndex || startOfLineIndex > endOfLineIndex + 1)
      {
        startOfLineIndex = endOfLineIndex ;
      }

      remainingValue = remainingValue.substring(startOfLineIndex) ;
    }

    lines.push(remainingValue) ;
    return lines.join('\n') ;
  }

  onTaskClick() {
    this.onTaskSelectClick.emit(this.taskSelect()!.index);
  }

  onTouchStart() {
    this.onTaskSelectTouchStart.emit({ position: this.position(), idx: this.idx() }) ;
  }

  onTouchEnd($event: TouchEvent) {
    this.onTaskSelectTouchEnd.emit({ position: this.position(), idx: this.idx(), taskIdx: this.taskSelect()!.index, $event });
  }

  onMouseOver() {
    this.onTaskMouseOver.emit({ position: this.position(), idx: this.idx() });
  }

  onMouseLeave() {
    this.onTaskMouseLeave.emit({ position: this.position(), idx: this.idx() });
  }
}
